"use client"

import { AlertTriangle, Bot, Coins, Cpu, XCircle } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { DendromapLoader } from "../../components/dendromap-loader"
import { Alert, AlertDescription } from "../../components/ui/alert"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { useAiUsageReport } from "../../hooks/api/superadmin/use-superadmin-client"
import { useLabels } from "../../hooks/use-labels"
import { cn } from "../../lib/utils"
import { PageHeader } from "../../ui/layout/page-header"
import { MetricCard } from "../dashboard/components/metric-card"
import { formatEuros, formatTokens, monthLabel, quotaLevel, recentMonths } from "./ai-usage.helpers"
import { apiErrorMessage } from "./client-detail.helpers"

const QUOTA_BAR_CLASSES = {
  none: "",
  ok: "bg-primary",
  warning: "bg-warning",
  reached: "bg-destructive",
} as const

/** Espace /superadmin : consommation de l'IA et coût estimé, par client et par mois. */
export function AiUsagePage() {
  const { labels } = useLabels()
  const months = recentMonths()
  const [month, setMonth] = useState(months[0]!)
  const { data: report, isLoading, error } = useAiUsageReport(month)

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Title>{labels.sidebar.superadmin.aiUsage}</PageHeader.Title>
        <PageHeader.Description>
          Analyses photo par client, quota mensuel et coût estimé à partir des jetons et des prix saisis dans les{" "}
          <Link to="/superadmin/ai/settings" className="underline underline-offset-2">paramètres IA</Link>.
        </PageHeader.Description>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-48" aria-label="Mois"><SelectValue /></SelectTrigger>
          <SelectContent>
            {months.map((value) => <SelectItem key={value} value={value}>{monthLabel(value)}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      {isLoading ? (
        <DendromapLoader message="Chargement de la consommation..." />
      ) : error || !report ? (
        <Alert variant="destructive"><AlertDescription>{apiErrorMessage(error, "Rapport indisponible.")}</AlertDescription></Alert>
      ) : (
        <>
          {report.prices.price_input_per_million === 0 && report.prices.price_output_per_million === 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Aucun prix renseigné : les coûts affichés valent 0. Indiquez le tarif du fournisseur dans les{" "}
                <Link to="/superadmin/ai/settings" className="underline underline-offset-2">paramètres IA</Link>.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard title="Analyses facturées" value={report.totals.calls} description={`${report.totals.clients} client(s)`} icon={Bot} iconColor="text-primary" iconBgColor="bg-primary/10" />
            <MetricCard title="Coût estimé" value={formatEuros(report.totals.cost)} icon={Coins} iconColor="text-primary" iconBgColor="bg-primary/10" />
            <MetricCard
              title="Jetons"
              value={formatTokens(report.totals.promptTokens + report.totals.completionTokens)}
              description={`${formatTokens(report.totals.promptTokens)} en entrée · ${formatTokens(report.totals.completionTokens)} en sortie`}
              icon={Cpu}
              iconColor="text-primary"
              iconBgColor="bg-primary/10"
            />
            <MetricCard title="Échecs" value={report.totals.errors} description="Non facturés, hors quota" icon={XCircle} iconColor="text-destructive" iconBgColor="bg-destructive/10" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Par client — {monthLabel(report.month)}</CardTitle>
              <CardDescription>
                Quota global : {report.globalLimit > 0 ? `${report.globalLimit} analyses / mois` : "illimité"}. Un plafond propre à un
                client se règle dans sa fiche.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {report.clients.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune analyse IA ce mois-ci.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Analyses</TableHead>
                      <TableHead className="w-48">Quota</TableHead>
                      <TableHead className="text-right">Échecs</TableHead>
                      <TableHead className="text-right">Jetons (entrée / sortie)</TableHead>
                      <TableHead className="text-right">Coût estimé</TableHead>
                      <TableHead className="text-right">Avis utiles</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.clients.map((client) => {
                      const level = quotaLevel(client.usageRatio)
                      const rated = client.feedback.useful + client.feedback.notUseful
                      return (
                        <TableRow key={client.groupeId ?? "none"}>
                          <TableCell className="font-medium">
                            {client.groupeId === null ? (
                              client.name
                            ) : (
                              <Link to={`/superadmin/clients/${client.groupeId}`} className="hover:underline">{client.name}</Link>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{client.calls}</TableCell>
                          <TableCell>
                            {client.groupeId === null ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : client.limit <= 0 ? (
                              <span className="text-xs text-muted-foreground">Illimité{client.limitSource === "client" ? " (client)" : ""}</span>
                            ) : (
                              <div className="space-y-1">
                                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                                  <div className={cn("h-full", QUOTA_BAR_CLASSES[level])} style={{ width: `${Math.min(100, Math.round((client.usageRatio ?? 0) * 100))}%` }} />
                                </div>
                                <p className={cn("text-xs", level === "reached" ? "text-destructive" : "text-muted-foreground")}>
                                  {client.calls} / {client.limit}
                                  {client.limitSource === "client" ? " (plafond client)" : ""}
                                </p>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{client.errors}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {formatTokens(client.promptTokens)} / {formatTokens(client.completionTokens)}
                          </TableCell>
                          <TableCell className="text-right font-medium">{formatEuros(client.cost)}</TableCell>
                          <TableCell className="text-right">{rated > 0 ? `${Math.round((client.feedback.useful / rated) * 100)} % (${rated})` : "—"}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
